import { useEffect, useState, useTransition } from "react";
import { FileText, UploadCloud } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { useDict } from "@/lib/i18n/context";
import { resolveApiError } from "@/lib/api-error";
import { cn } from "@/lib/utils";
import type { CvVisibility } from "@/lib/types";

type ProfilePayload = {
  user_id: string;
  full_name: string | null;
  email: string;
  visibility: CvVisibility;
  has_cv: boolean;
  cv_url: string | null;
  updated_at: string;
};

export function CandidateProfilePage() {
  const t = useDict();
  const [profile, setProfile] = useState<ProfilePayload | null>(null);
  const [visibility, setVisibility] = useState<CvVisibility>("private");
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [pending, startTransition] = useTransition();

  function load() {
    setLoading(true);
    fetch("/api/candidate/profile", { credentials: "include" })
      .then(async (res) => {
        if (!res.ok) throw new Error("fail");
        return res.json() as Promise<{ profile: ProfilePayload }>;
      })
      .then((data) => {
        setProfile(data.profile);
        setVisibility(data.profile.visibility);
      })
      .catch(() => setError(t.profile.loadError))
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function onSave(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setMessage(null);
    if (file && (file.type !== "application/pdf" || file.size > 5 * 1024 * 1024)) {
      setError(t.profile.pdfOnly);
      return;
    }
    startTransition(async () => {
      const fd = new FormData();
      fd.set("visibility", visibility);
      if (file) fd.set("file", file, file.name);
      const res = await fetch("/api/candidate/profile", {
        method: "POST",
        credentials: "include",
        body: fd,
      });
      const data = (await res.json()) as {
        error?: string;
        code?: string;
        profile?: ProfilePayload;
      };
      if (!res.ok || data.error || !data.profile) {
        setError(
          data.error
            ? resolveApiError(t, data, "generic")
            : t.profile.saveError,
        );
        return;
      }
      setProfile(data.profile);
      setVisibility(data.profile.visibility);
      setFile(null);
      setMessage(t.profile.saved);
    });
  }

  if (loading) {
    return <p className="text-muted">{t.reports.loading}</p>;
  }

  return (
    <div className="mx-auto max-w-xl space-y-6">
      <div>
        <h1 className="text-2xl font-bold">{t.profile.title}</h1>
        <p className="mt-1 text-muted">{t.profile.subtitle}</p>
      </div>

      <Card>
        <CardContent className="space-y-5 pt-6">
          <form onSubmit={onSave} className="space-y-5">
            <div className="rounded-xl border border-border bg-surface/40 p-4">
              <div className="flex items-start gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-brand-50 text-brand-600 dark:bg-brand-950 dark:text-brand-300">
                  <FileText className="h-5 w-5" />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="font-medium">{t.profile.current}</p>
                  {profile?.has_cv && profile.cv_url ? (
                    <a
                      href={profile.cv_url}
                      target="_blank"
                      rel="noreferrer"
                      className="mt-1 inline-block text-sm font-medium text-brand-700 hover:underline dark:text-brand-300"
                    >
                      {t.profile.download}
                    </a>
                  ) : (
                    <p className="mt-1 text-sm text-muted">{t.profile.noCv}</p>
                  )}
                </div>
              </div>
            </div>

            <div>
              <Label htmlFor="cv">
                {profile?.has_cv ? t.profile.replace : t.profile.upload}
              </Label>
              <label
                htmlFor="cv"
                className="mt-1.5 flex h-11 cursor-pointer items-center gap-3 rounded-xl border border-dashed border-border bg-card px-4 text-sm text-muted hover:bg-surface"
              >
                <UploadCloud className="h-4 w-4 shrink-0" />
                <span className="truncate">
                  {file?.name ||
                    (profile?.has_cv ? t.profile.replace : t.profile.upload)}
                </span>
                <input
                  id="cv"
                  type="file"
                  accept="application/pdf,.pdf"
                  className="sr-only"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                />
              </label>
              <p className="mt-1.5 text-xs text-muted">{t.profile.pdfOnly}</p>
            </div>

            <div>
              <Label>{t.profile.visibility}</Label>
              <div className="mt-2 grid gap-2 sm:grid-cols-2">
                <button
                  type="button"
                  onClick={() => setVisibility("private")}
                  className={cn(
                    "rounded-xl border px-3 py-2.5 text-start text-sm transition",
                    visibility === "private"
                      ? "border-brand-500 bg-brand-50 dark:bg-brand-950"
                      : "border-border hover:bg-surface",
                  )}
                >
                  {t.profile.private}
                </button>
                <button
                  type="button"
                  onClick={() => setVisibility("public")}
                  className={cn(
                    "rounded-xl border px-3 py-2.5 text-start text-sm transition",
                    visibility === "public"
                      ? "border-brand-500 bg-brand-50 dark:bg-brand-950"
                      : "border-border hover:bg-surface",
                  )}
                >
                  {t.profile.public}
                </button>
              </div>
            </div>

            {error && <p className="text-sm text-red-600">{error}</p>}
            {message && <p className="text-sm text-emerald-700">{message}</p>}

            <Button type="submit" loading={pending} className="w-full">
              {t.profile.save}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
